import { useMutation, useQuery } from '@apollo/client/react';
import { useState } from 'react';

import { ROUTES } from '@repo/common';
import {
  BillingHistoryDocument,
  BillingPlanSummaryDocument,
  ContactTopic,
  DataTestIds,
  RefundEligibilityDocument,
  type RefundEligibilityQuery,
  type RefundRequestReasonCode,
  RequestMyRefundDocument,
  type RequestMyRefundMutation,
} from '@repo/frontend-shared';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';
import { toast } from '@repo/sds/utils/toast';
import { ContactSupportDialog } from '@repo/ui/components/contact-support/contact-support-dialog';

import { RefundEligibilityFacts, RefundPackageSummary } from './refund-eligibility-facts';
import { asksForReasonDetail, describeIneligibility } from './refund-format';
import { RefundPolicyNote } from './refund-policy-link';
import { RefundReasonFields } from './refund-reason-fields';

type RefundEligibility = NonNullable<RefundEligibilityQuery['myRefundEligibility']>;

/** The purchase being refunded, as the package card already names it. */
export interface RefundSubject {
  productTitle: string;
  planLabel: string;
}

interface RefundRequestDialogProps {
  isOpen: boolean;
  subject: RefundSubject;
  onClose(): void;
}

function describeRefundError(error: unknown): string {
  return error instanceof Error ? error.message : 'Please try again.';
}

/**
 * The refund a member asks for themselves.
 *
 * One dialog rather than one per outcome: the facts on screen are the same
 * either way, and only the closing paragraph and the buttons change. The
 * server decides eligibility again when the request arrives, so what is shown
 * here is an explanation rather than the gate.
 */
export function RefundRequestDialog({ isOpen, subject, onClose }: RefundRequestDialogProps) {
  const [reasonCode, setReasonCode] = useState<RefundRequestReasonCode | undefined>(undefined);
  const [reasonDetail, setReasonDetail] = useState('');
  const [isContactingSupport, setIsContactingSupport] = useState(false);

  const { data, loading, error } = useQuery<RefundEligibilityQuery>(RefundEligibilityDocument, {
    fetchPolicy: 'cache-and-network',
    skip: !isOpen,
  });
  const eligibility = data?.myRefundEligibility ?? undefined;

  const [requestRefund, { loading: isSubmitting }] = useMutation<RequestMyRefundMutation>(RequestMyRefundDocument, {
    refetchQueries: [BillingPlanSummaryDocument, BillingHistoryDocument],
    onCompleted: () => {
      toast.success('Your refund has been submitted.');
      onClose();
    },
    onError: (mutationError: Error) => {
      toast.error(`Refund could not be completed: ${describeRefundError(mutationError)}`);
    },
  });

  const handleClose = (): void => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  const handleConfirm = (): void => {
    void requestRefund({
      variables: {
        input: {
          // One key per attempt at this dialog, so a double-click and a retry
          // are the same request to Stripe rather than two refunds.
          requestKey: crypto.randomUUID(),
          reasonCode,
          // The detail box exists only for "Other", so text typed there and
          // left behind by switching reason is not sent as if it explained one.
          reasonDetail: asksForReasonDetail(reasonCode) ? reasonDetail.trim() || undefined : undefined,
        },
      },
    });
  };

  if (isContactingSupport && eligibility) {
    return (
      <ContactSupportDialog
        isOpen={isOpen}
        topic={ContactTopic.CANCELLATIONS_AND_REFUNDS}
        pagePath={ROUTES.BILLING}
        initialMessage={buildSupportMessage(subject, eligibility)}
        // Cancel goes back to the refusal, which still explains what happened.
        onClose={() => {
          setIsContactingSupport(false);
        }}
        onSent={onClose}
      />
    );
  }

  return (
    <SDSDialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Request a refund?"
      size="small"
      className="BillingDialog"
      dataTestId={DataTestIds.BILLING.REFUND.DIALOG}
      primaryAction={buildPrimaryAction(eligibility, isSubmitting, handleConfirm, () => {
        setIsContactingSupport(true);
      })}
      secondaryAction={
        isSubmitting ? undefined : { label: eligibility?.isEligible ? 'Cancel' : 'Close', onClick: handleClose }
      }
    >
      <RefundDialogBody
        subject={subject}
        eligibility={eligibility}
        isLoading={loading && !data}
        hasError={Boolean(error)}
        isSubmitting={isSubmitting}
        reasonCode={reasonCode}
        reasonDetail={reasonDetail}
        onChangeReasonCode={setReasonCode}
        onChangeReasonDetail={setReasonDetail}
      />
    </SDSDialog>
  );
}

/**
 * The opening text of a support request from a refused refund: which purchase,
 * and what the page told the member, so support starts with the context.
 */
function buildSupportMessage(subject: RefundSubject, eligibility: RefundEligibility): string {
  const refusal = describeIneligibility(
    eligibility.ineligibilityReason,
    eligibility.completionLimitPercent,
    eligibility.windowDays,
  );
  return `I would like to ask about a refund for my ${subject.productTitle} purchase (${subject.planLabel}).\n\nThe billing page said: ${refusal}`;
}

/**
 * What the one button on the right does.
 *
 * An ineligible refund offers support rather than a disabled confirm: there is
 * nothing to retry, and a greyed-out button answers none of the question the
 * member now has.
 */
function buildPrimaryAction(
  eligibility: RefundEligibility | undefined,
  isSubmitting: boolean,
  onConfirm: () => void,
  onContactSupport: () => void,
) {
  if (!eligibility) {
    return undefined;
  }
  if (!eligibility.isEligible) {
    return {
      label: 'Contact support',
      // The same route as the public Get in Touch form, so the request lands
      // with support whatever the member's mail setup.
      onClick: onContactSupport,
      dataTestId: DataTestIds.BILLING.REFUND.CONTACT_SUPPORT,
    };
  }
  return {
    label: isSubmitting ? 'Processing…' : 'Confirm refund',
    onClick: onConfirm,
    loading: isSubmitting,
    dataTestId: DataTestIds.BILLING.REFUND.CONFIRM,
  };
}

function RefundDialogBody({
  subject,
  eligibility,
  isLoading,
  hasError,
  isSubmitting,
  reasonCode,
  reasonDetail,
  onChangeReasonCode,
  onChangeReasonDetail,
}: {
  subject: RefundSubject;
  eligibility: RefundEligibility | undefined;
  isLoading: boolean;
  hasError: boolean;
  isSubmitting: boolean;
  reasonCode?: RefundRequestReasonCode;
  reasonDetail: string;
  onChangeReasonCode(reasonCode: RefundRequestReasonCode | undefined): void;
  onChangeReasonDetail(reasonDetail: string): void;
}) {
  if (isLoading) {
    return (
      <SDSText
        type="body-medium"
        text="Checking whether this purchase can be refunded…"
        color="text-secondary"
        dataTestId={DataTestIds.BILLING.REFUND.LOADING_STATE}
      />
    );
  }

  // Error before not-found: a failed read has not established that this
  // purchase cannot be refunded.
  if (hasError || !eligibility) {
    return (
      <SDSText
        type="body-medium"
        text="We could not check this purchase right now. Please try again."
        color="alert"
        dataTestId={DataTestIds.BILLING.REFUND.ERROR_STATE}
      />
    );
  }

  return (
    // 24px between every block, as in each refund frame; SDS has no token for
    // the frame's content/regular gap.
    <SDSFlexbox direction="column" gap="no-design-24" fullWidth>
      <RefundPackageSummary productTitle={subject.productTitle} planLabel={subject.planLabel} />

      <RefundEligibilityFacts
        windowDays={eligibility.windowDays}
        daysRemaining={eligibility.daysRemaining}
        completionRatePercent={eligibility.completionRatePercent}
        completionLimitPercent={eligibility.completionLimitPercent}
        amountLabel={eligibility.isEligible ? 'Refund amount' : 'Amount paid'}
        amount={eligibility.isEligible ? eligibility.refundAmount : eligibility.amountPaid}
        refundToLabel={eligibility.isEligible ? eligibility.refundToLabel : undefined}
      />

      <SDSFlexbox direction="row" className="RefundNotice" fullWidth dataTestId={DataTestIds.BILLING.REFUND.NOTICE}>
        <SDSText
          type="body-large"
          color="text-secondary"
          text={
            eligibility.isEligible
              ? 'Once your refund is initiated, your Premium MBA access will end immediately.'
              : describeIneligibility(
                  eligibility.ineligibilityReason,
                  eligibility.completionLimitPercent,
                  eligibility.windowDays,
                )
          }
        />
      </SDSFlexbox>

      <RefundPolicyNote
        layout="inline"
        dataTestId={DataTestIds.BILLING.REFUND.POLICY_NOTE}
        linkDataTestId={DataTestIds.BILLING.REFUND.POLICY_LINK}
      />

      {eligibility.isEligible ? (
        <RefundReasonFields
          reasonCode={reasonCode}
          reasonDetail={reasonDetail}
          isDisabled={isSubmitting}
          onChangeReasonCode={onChangeReasonCode}
          onChangeReasonDetail={onChangeReasonDetail}
        />
      ) : null}
    </SDSFlexbox>
  );
}
